import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import BasicModalBody from '../BasicModal/BasicModalBody';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import useProductPrice from '../../util/hooks/useProductPrice';
import themeColors from '../../assets/styles/themeColors';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import { uFlexColumn } from '../../assets/styles/utility';
import { selectPriceAlerts } from '../../store/selectors/priceAlertsSelectors';
import {
  deletePriceAlert,
  fetchPriceAlerts,
  dismissPriceAlertBanner,
  clearExpiredPriceAlerts,
} from '../../store/actions/priceAlerts/priceAlertsActions';
import { fetchProductsWithPrices } from '../../store/actions/products/productActions';
import {
  getCurrentAccountUid,
  getIsReadOnlyUser,
} from '../../store/selectors/accountSelectors';
import { useBrokerCode } from '../../util/hooks/useBrokerCode';
import {
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import { PRICE_ALERT_READ_ONLY_BANNER_DISMISSED } from '../../constants/sessionStorage';
import ReadOnlyAccessModal from './ReadOnlyAccessModal';
import ErrorDrawer from '../Buy/ErrorDrawer';
import useErrorDrawer from '../../util/hooks/useErrorDrawer';
import { ReactComponent as CheckedBlue } from '../../assets/images/priceAlerts/checked-blue.svg';
import { ReactComponent as CloseTooltipIcon } from '../../assets/images/priceAlerts/close-tooltip.svg';
import { ReactComponent as InfoBlueIcon } from '../../assets/images/priceAlerts/info-blue.svg';
import { ReactComponent as AlertCloseIcon } from '../../assets/images/priceAlerts/alert-close-icon.svg';
import Button from '../Button/Button';
import LoaderIcon from '../Loader/LoaderIcon';
import ActiveAlerts, { AddAnotherAlertButton } from './ActiveAlerts';
import TriggeredAlerts, { TriggeredAlertsHeader } from './TriggeredAlerts';
import ExpiredAlerts, { ExpiredAlertsHeader } from './ExpiredAlerts';
import {
  ProductName,
  ProductPrice,
  ProductHeadingRow,
  ProductHeadingPrice,
  Divider as CommonDivider,
  FullWidthButtonWrapper,
} from './Common';
import { priceAlertStatuses } from '../../util/enum/api/priceAlertStatusTypes';
import useGtmHook from '../../util/hooks/useGtmHook';
import {
  trackBuyStarted,
  trackPriceAlertCancelled,
  trackPriceAlertBulkCleared,
  ENTRY_POINTS,
} from './Common/gtmEventHelper';

const TABS = ['Active', 'Triggered', 'Expired'];

// Whole days elapsed from an alert's submission date to now, for analytics.
const daysActive = (fromDate) => {
  if (!fromDate) return null;
  const start = new Date(fromDate);
  if (Number.isNaN(start.getTime())) return null;
  const elapsedMs = Date.now() - start.getTime();
  return Math.max(0, Math.floor(elapsedMs / (1000 * 60 * 60 * 24)));
};

const TabBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 46px;
  padding: 6px;
  margin-bottom: ${({ $noMarginBottom }) =>
    $noMarginBottom ? pxToRem(6) : pxToRem(16)};
  background: ${themeColors.colorTabBarBackground};
  border-radius: 4px;
  opacity: 1;
  box-sizing: border-box;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    gap: 4px;
    border-radius: 8px;
  }
`;

const Tab = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${pxToRem(6)};
  flex: 1;
  height: 34px;
  padding: 0 ${pxToRem(16)};
  background: ${({ active }) => (active ? themeColors.colorWhite : 'none')};
  border: ${({ active }) =>
    active ? `1px solid ${themeColors.colorLinkPrimary}` : 'none'};
  border-radius: ${pxToRem(4)};
  color: ${({ active }) =>
    active ? themeColors.colorLinkPrimary : themeColors.colorTextSecondary};
  font-family: ${variables.family.goldSilver};
  font-size: 16px;
  line-height: 14px;
  font-weight: 500;
  cursor: pointer;
  box-sizing: border-box;
  transition: border-color 0.15s, color 0.15s;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    padding: 0 12px;
    border-radius: 6px;
    font-size: 14px;
    line-height: 14px;
  }
`;

const TriggeredDivider = styled(CommonDivider)`
  margin: ${pxToRem(12)} ${pxToRem(-16)} 0;
`;

const BuyNowButton = styled(Button)`
  margin-top: ${pxToRem(16)};
  height: 44px;

  && {
    font-size: 16px;
  }
`;

const PriceDisclaimer = styled.p`
  margin: ${pxToRem(8)} 0 0;
  font-family: ${variables.family.goldSilver};
  font-weight: 400;
  font-style: italic;
  font-size: 14px;
  line-height: 100%;
  color: ${themeColors.colorTextSecondary};
  text-align: center;
`;

const StyledCloseTooltipIcon = styled(CloseTooltipIcon)`
  width: 14.63px;
  height: 14.63px;
  flex-shrink: 0;
`;

const BannerLoaderIcon = styled(LoaderIcon)`
  width: 14.63px;
  height: 14.63px;
  border-width: 2px;
  border-bottom-color: ${themeColors.colorWhite};
  border-top-color: ${themeColors.colorWhite};
  flex-shrink: 0;
`;

const TabHelperBanner = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: ${pxToRem(8)};
  padding: 16px;
  margin-bottom: ${pxToRem(12)};
  background-color: ${themeColors.colorBackgroundSecondary};
  border: 1px solid ${themeColors.colorCardBorder};
  border-radius: ${pxToRem(8)};
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.2);
  color: ${themeColors.colorTextSecondary};
  font-family: ${variables.family.goldSilver};
  font-size: 14px;
  font-weight: 400;
  line-height: 140%;

  /* Upward caret/pointer on the top-left (Figma "Arrow" layer: 12 x 7, left
     42px, overlapping the banner top by 1px so it merges seamlessly). */
  &::before {
    content: '';
    position: absolute;
    bottom: calc(100% - 1px);
    left: ${pxToRem(90)};
    width: 12px;
    height: 7px;
    top: -9px;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 7px solid ${themeColors.colorBackgroundSecondary};
  }
`;

const TabHelperText = styled.span`
  flex: 1;
`;

const TabHelperInfoIcon = styled(InfoBlueIcon)`
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  margin-top: 2px;
`;

const TabHelperCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 3px;
  margin-top: 2px;
  flex-shrink: 0;
  border: none;
  background: none;
  cursor: pointer;

  svg {
    width: 10px;
    height: 10px;
  }
`;

const PriceTriggeredBannerWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
`;

const PriceTriggeredBanner = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 2px 6px;
  border-radius: ${variables.borderRadius.borderRadiusSm};
  background-color: ${themeColors.colorBestFlag};
  color: ${themeColors.colorWhite};
  font-family: ${variables.family.goldSilver};
  font-size: 16px;
  font-weight: 500;
  line-height: 12px;
  white-space: nowrap;
  gap: 6px;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    height: 22px;
    font-size: 14px;
  }

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 6px solid ${themeColors.colorBestFlag};
  }
`;

const ScrollableModalBody = styled(BasicModalBody)`
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const FixedHeader = styled.div`
  ${uFlexColumn};
  flex-shrink: 0;
  background: ${themeColors.colorWhite};
  /* full-bleed so scrolling full-width rows (ActiveAlerts list uses negative
     margins) pass cleanly under it, matching the modal body's 16px padding */
  margin: 0 -${pxToRem(16)};
  padding: 0 ${pxToRem(16)};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    margin: 0 -${pxToRemMd(16)};
    padding: 0 ${pxToRemMd(16)};
  }
`;

const TabContent = styled.div`
  ${uFlexColumn};
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: ${({ $noMarginTop }) => ($noMarginTop ? 0 : pxToRem(16))};
  /* full-bleed so the scrollbar sits at the modal's true edge instead of
     inset by the modal body's 16px padding */
  margin-right: -${pxToRem(16)};
  padding-right: ${pxToRem(16)};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    margin-right: -${pxToRemMd(16)};
    padding-right: ${pxToRemMd(16)};
  }
`;

const TabHeaderSpacer = styled.div`
  margin-top: ${pxToRem(16)};
`;

const CloseButtonWrapper = styled(FullWidthButtonWrapper)`
  button {
    width: 100%;
    border-radius: 4px;
    font-size: 16px;
    font-weight: 600;
    line-height: 100%;
  }
  flex-shrink: 0;
  margin-top: auto;
  padding-top: ${pxToRem(16)};
  background: ${themeColors.colorWhite};
  /* full-bleed background matching FixedHeader so the pinned footer covers the
     full modal width as rows scroll beneath it */
  margin-left: -${pxToRem(16)};
  margin-right: -${pxToRem(16)};
  padding-left: ${pxToRem(16)};
  padding-right: ${pxToRem(16)};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    margin-left: -${pxToRemMd(16)};
    margin-right: -${pxToRemMd(16)};
    padding-left: ${pxToRemMd(16)};
    padding-right: ${pxToRemMd(16)};
  }
`;

const AllAlertsList = ({
  wizardData,
  setWizardData,
  setWizardTitle,
  isProductPage,
  handleNext,
  closeModal,
  onBuyNow,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(
    wizardData?.initialTab || 'Active',
  );
  const [isDismissingBanner, setIsDismissingBanner] = useState(false);
  // Per-tab dismissal of the helper banner. Local state so it persists while
  // switching tabs but resets when the modal is closed and reopened.
  const [dismissedTabHelpers, setDismissedTabHelpers] = useState({});
  const { storageProduct } = wizardData;
  const locations = wizardData?.storageProduct?.Locations || [];
  const priceAlerts = useSelector(selectPriceAlerts);
  const accountUid = useSelector(getCurrentAccountUid);
  const isReadOnly = useSelector(getIsReadOnlyUser);
  const dispatch = useDispatch();
  const brokerCode = useBrokerCode();
  const { ecommerceGtmEvent } = useGtmHook();

  const [readOnlyModalOpen, setReadOnlyModalOpen] = useState(false);
  const onBlockedAction = () => setReadOnlyModalOpen(true);
  // Surface BE validation errors (73449) for delete / clear-all in an ErrorDrawer.
  const {
    isOpen: isActionErrorOpen,
    errorTitle: actionErrorTitle,
    errorMessage: actionErrorMessage,
    showError: showActionError,
    hideError: hideActionError,
  } = useErrorDrawer();
  // Read-only helper banner dismissal persists for the browser session
  // (dedicated key, not the WIZARD blob which is cleared when the drawer closes).
  const [readOnlyBannerDismissed, setReadOnlyBannerDismissed] = useState(
    () =>
      retrieveFromSessionStorage(PRICE_ALERT_READ_ONLY_BANNER_DISMISSED) ===
      true,
  );
  const dismissReadOnlyBanner = () => {
    storeInSessionStorage(PRICE_ALERT_READ_ONLY_BANNER_DISMISSED, true);
    setReadOnlyBannerDismissed(true);
  };

  const mapAlert = (alert) => {
    let alertMethod = 'Email';
    if (alert.NotifyViaEmail && alert.NotifyViaSMS)
      alertMethod = 'Email + Text';
    else if (alert.NotifyViaSMS) alertMethod = 'Text';
    return {
      PriceAlertUid: alert.PriceAlertUid,
      alertPrice: formatMoneyNumeral(alert.TargetPrice),
      alertDate: alert.SubmittedOn,
      alertExpiryDate: alert.ExpiresOn,
      alertTriggeredDate: alert?.TriggeredOn,
      alertMethod,
      TargetPrice: alert.TargetPrice,
      Phone: alert.Phone,
      NotifyViaSMS: !!alert.NotifyViaSMS,
      IsTriggeredBannerDismissed: !!alert.IsTriggeredBannerDismissed,
    };
  };

  const productAlerts = useMemo(
    () =>
      priceAlerts.filter(
        (alert) => alert.ProductCode === storageProduct?.ProductCode,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [priceAlerts, storageProduct?.ProductCode],
  );

  const activeAlerts = useMemo(
    () =>
      productAlerts
        .filter((alert) => alert.PriceAlertStatus === priceAlertStatuses.Active)
        .map(mapAlert),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productAlerts],
  );

  const triggeredAlerts = useMemo(
    () =>
      productAlerts
        .filter(
          (alert) => alert.PriceAlertStatus === priceAlertStatuses.Triggered,
        )
        .map((alert) => {
          const triggeredDate = alert.TriggeredOn
            ? new Date(alert.TriggeredOn)
            : null;
          const alertTime =
            triggeredDate && !Number.isNaN(triggeredDate.getTime())
              ? triggeredDate.toLocaleTimeString(undefined, {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })
              : null;
          return {
            ...mapAlert(alert),
            alertDate: alert.TriggeredOn,
            alertTime,
          };
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productAlerts],
  );
  const isTriggeredBannerDismissed = triggeredAlerts.some(
    (alert) => alert.IsTriggeredBannerDismissed,
  );
  const expiredAlerts = useMemo(
    () =>
      productAlerts
        .filter(
          (alert) => alert.PriceAlertStatus === priceAlertStatuses.Expired,
        )
        .map((alert) => {
          const mappedAlert = mapAlert(alert);
          return {
            ...mappedAlert,
          };
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productAlerts],
  );

  useEffect(() => {
    setWizardTitle(t('gtt.title.myAlerts'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const minPrice = locations?.length
    ? Math.min(...locations.map((l) => l.PricePerUnit))
    : 0;
  const firstLowestVault = locations.find(
    (l) => l.PricePerUnit === minPrice && l.LocationType !== 12,
  );

  const vaultPriceLocation = wizardData?.selectedVault ?? firstLowestVault;

  const unitPrice = useProductPrice(
    storageProduct,
    vaultPriceLocation,
    isProductPage,
    true,
  );

  const showTriggeredBanner =
    activeTab === 'Active' &&
    !!triggeredAlerts?.length &&
    !isTriggeredBannerDismissed;

  const tabHelperTextMap = {
    Active: t('gtt.labels.tabHelperActive'),
    Triggered: t('gtt.labels.tabHelperTriggered'),
    Expired: t('gtt.labels.tabHelperExpired'),
  };
  const showTabHelper = !dismissedTabHelpers[activeTab];

  const showActiveHeader = activeTab === 'Active';
  const showTriggeredHeader =
    activeTab === 'Triggered' && !!triggeredAlerts?.length;
  const showExpiredHeader = activeTab === 'Expired' && !!expiredAlerts?.length;
  const showTabHeader =
    showActiveHeader || showTriggeredHeader || showExpiredHeader;

  return (
    <ScrollableModalBody>
      <FixedHeader>
        <TabBar $noMarginBottom={showTriggeredBanner}>
          {TABS.map((tab) => (
            <Tab
              key={tab}
              active={activeTab === tab}
              onClick={() => {
                setActiveTab(tab);
                setWizardData({ initialTab: tab });
              }}
              type="button"
            >
              {activeTab === tab && (
                <CheckedBlue style={{ width: 20, height: 20 }} />
              )}
              {tab}
            </Tab>
          ))}
        </TabBar>

        {showTriggeredBanner && (
          <PriceTriggeredBannerWrapper>
            <PriceTriggeredBanner>
              {t('gtt.bell.priceTriggered')}
              {isDismissingBanner ? (
                <BannerLoaderIcon />
              ) : (
                <StyledCloseTooltipIcon
                  onClick={() =>
                    dispatch(
                      dismissPriceAlertBanner({
                        accountUid,
                        productCode: storageProduct?.ProductCode,
                        setIsLoading: setIsDismissingBanner,
                        onSuccess: () => {
                          dispatch(
                            fetchPriceAlerts({
                              accountUid,
                              productCode: storageProduct?.ProductCode,
                            }),
                          );
                        },
                      }),
                    )
                  }
                  style={{ cursor: 'pointer' }}
                />
              )}
            </PriceTriggeredBanner>
          </PriceTriggeredBannerWrapper>
        )}

        {showTabHelper && (
          <TabHelperBanner>
            <TabHelperInfoIcon />
            <TabHelperText>{tabHelperTextMap[activeTab]}</TabHelperText>
            <TabHelperCloseButton
              type="button"
              onClick={() =>
                setDismissedTabHelpers((prev) => ({
                  ...prev,
                  [activeTab]: true,
                }))
              }
            >
              <AlertCloseIcon />
            </TabHelperCloseButton>
          </TabHelperBanner>
        )}

        {isReadOnly && !readOnlyBannerDismissed && (
          <TabHelperBanner data-cy="price-alert-read-only-banner">
            <TabHelperInfoIcon />
            <TabHelperText>{t('gtt.readOnly.bannerText')}</TabHelperText>
            <TabHelperCloseButton onClick={dismissReadOnlyBanner} />
          </TabHelperBanner>
        )}

        {(activeTab !== 'Triggered' || !!triggeredAlerts?.length) &&
          (activeTab !== 'Expired' || !!expiredAlerts?.length) && (
            <ProductHeadingRow>
              <ProductName marginBottom={4} isGS={brokerCode === 'GS'}>
                {storageProduct?.ShortCaption}
              </ProductName>
              <ProductHeadingPrice>
                <ProductPrice>{formatMoneyNumeral(unitPrice)}</ProductPrice>
              </ProductHeadingPrice>
            </ProductHeadingRow>
          )}

        {activeTab === 'Triggered' && !!triggeredAlerts?.length && (
          <>
            <TriggeredDivider />
            <BuyNowButton
              variant="primary"
              isWidthS
              textTransform="none"
              lockStyle={isReadOnly}
              data-cy="button-price-alerts-buy-now"
              onClick={() => {
                if (isReadOnly) {
                  onBlockedAction();
                  return;
                }
                trackBuyStarted({
                  ecommerceGtmEvent,
                  alertId: triggeredAlerts[0]?.PriceAlertUid,
                  productName: storageProduct?.ShortCaption,
                  entryPoint: ENTRY_POINTS.ACTIVE_ALERTS,
                });
                if (onBuyNow)
                  onBuyNow(storageProduct, triggeredAlerts[0]?.PriceAlertUid);
              }}
            >
              {t('gtt.common.buyNowAt', {
                price: formatMoneyNumeral(unitPrice),
              })}
            </BuyNowButton>
            <PriceDisclaimer>{t('gtt.labels.priceDisclaimer')}</PriceDisclaimer>
          </>
        )}

        {showActiveHeader && (
          <TabHeaderSpacer>
            <AddAnotherAlertButton
              isReadOnly={isReadOnly}
              onBlockedAction={onBlockedAction}
              onAddAlert={() => {
                setWizardData({
                  orderValues: {},
                  isEditAlert: false,
                  priceAlertUid: null,
                  isSmsChecked: false,
                  phone: null,
                });
                handleNext();
              }}
            />
          </TabHeaderSpacer>
        )}
        {showTriggeredHeader && (
          <TabHeaderSpacer>
            <TriggeredAlertsHeader count={triggeredAlerts.length} />
          </TabHeaderSpacer>
        )}
        {showExpiredHeader && (
          <TabHeaderSpacer>
            <ExpiredAlertsHeader
              count={expiredAlerts.length}
              isReadOnly={isReadOnly}
              onBlockedAction={onBlockedAction}
              onClearAll={() => {
                trackPriceAlertBulkCleared({
                  ecommerceGtmEvent,
                  countCleared: expiredAlerts.length,
                  alertIds: expiredAlerts.map((a) => a.PriceAlertUid),
                });
                dispatch(
                  clearExpiredPriceAlerts({
                    accountUid,
                    productCode: storageProduct?.ProductCode,
                    setError: (message) =>
                      message
                        ? showActionError({ message })
                        : hideActionError(),
                    onSuccess: () => {
                      dispatch(
                        fetchPriceAlerts({
                          accountUid,
                          ...(storageProduct?.ProductCode && {
                            productCode: storageProduct.ProductCode,
                          }),
                        }),
                      );
                    },
                  }),
                );
              }}
            />
          </TabHeaderSpacer>
        )}
      </FixedHeader>

      <TabContent $noMarginTop={showTabHeader}>
        {activeTab === 'Active' && (
          <ActiveAlerts
            userSetAlert={activeAlerts}
            isReadOnly={isReadOnly}
            onBlockedAction={onBlockedAction}
            onEditAlert={(alert) => {
              const numericPrice = alert.alertPrice.replace(/[^0-9.]/g, '');
              setWizardData({
                orderValues: { CashAmount: numericPrice },
                isEditAlert: true,
                priceAlertUid: alert.PriceAlertUid,
                // Deliberately NOT seeding isSmsChecked/phone from this
                // list-derived, possibly-stale `alert` object (see the
                // fetchPriceAlerts race in handleSubmitClick's handleNext).
                // AlertEntry's own fetchPriceAlert reconciliation
                // (isFreshPhoneApplied/SectionLoader) is the authoritative
                // source for Edit Alert, so seeding here is redundant and,
                // when stale, actively wrong. Explicitly clear any leftover
                // value from a different alert's prior session.
                isSmsChecked: undefined,
                phone: undefined,
              });
              handleNext();
            }}
            onDeleteAlert={(alert) => {
              trackPriceAlertCancelled({
                ecommerceGtmEvent,
                alertId: alert.PriceAlertUid,
                alertPrice: alert.TargetPrice,
                timeActiveDays: daysActive(alert.alertDate),
                // Cancelling is always done from the My Alerts list.
                source: ENTRY_POINTS.MY_ALERTS,
              });
              dispatch(
                deletePriceAlert({
                  accountUid,
                  priceAlertUid: alert.PriceAlertUid,
                  ProductCode: storageProduct?.ProductCode,
                  currentPrice: unitPrice,
                  setIsLoading: () => {},
                  setError: (message) =>
                    message ? showActionError({ message }) : hideActionError(),
                  onSuccess: () => {
                    dispatch(
                      fetchPriceAlerts({
                        accountUid,
                        ...(storageProduct?.ProductCode && {
                          productCode: storageProduct.ProductCode,
                        }),
                      }),
                    );
                    dispatch(
                      fetchProductsWithPrices({ accountUid, side: 'Buy' }),
                    );
                  },
                  handleNext: () => {},
                }),
              );
            }}
          />
        )}
        {activeTab === 'Triggered' && (
          <TriggeredAlerts alertsList={triggeredAlerts} />
        )}
        {activeTab === 'Expired' && (
          <ExpiredAlerts
            alertsList={expiredAlerts}
            isReadOnly={isReadOnly}
            onBlockedAction={onBlockedAction}
            onSetAgain={(alert) => {
              setWizardData({
                orderValues: { CashAmount: alert.TargetPrice },
                isEditAlert: false,
                priceAlertUid: null,
                isSmsChecked: alert.NotifyViaSMS,
                phone: alert.Phone,
                // Attribution for the price_alert_reactivated event, fired
                // from AlertEntry once the re-created alert saves.
                isReactivation: true,
                originalAlertUid: alert.PriceAlertUid,
                originalAlertPrice: alert.TargetPrice,
              });
              handleNext();
            }}
          />
        )}
      </TabContent>
      <CloseButtonWrapper>
        <Button
          variant="primaryOutlined"
          isWidthS
          textTransform="none"
          label={t('common.close')}
          onClick={closeModal}
        />
      </CloseButtonWrapper>
      <ReadOnlyAccessModal
        open={readOnlyModalOpen}
        onClose={() => setReadOnlyModalOpen(false)}
      />
      <ErrorDrawer
        open={isActionErrorOpen}
        errorTitle={actionErrorTitle}
        errorMessage={actionErrorMessage}
        onClose={hideActionError}
      />
    </ScrollableModalBody>
  );
};

AllAlertsList.propTypes = {
  wizardData: PropTypes.shape({
    storageProduct: PropTypes.shape({
      ShortCaption: PropTypes.string,
      ProductCode: PropTypes.string,
      SymbolCode: PropTypes.string,
      Locations: PropTypes.arrayOf(
        PropTypes.shape({
          PricePerUnit: PropTypes.number,
          BuyCCPricePerUnit: PropTypes.number,
          LocationType: PropTypes.number,
        }),
      ),
    }),
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
    }),
    initialTab: PropTypes.string,
  }),
  setWizardData: PropTypes.func,
  setWizardTitle: PropTypes.func,
  isProductPage: PropTypes.bool,
  handleNext: PropTypes.func,
  closeModal: PropTypes.func,
  onBuyNow: PropTypes.func,
};

export default AllAlertsList;
